'use client';

/**
 * React Query Provider
 * 
 * Single Responsibility: Configure and provide QueryClient to the app.
 * 
 * Configuration optimized for B2B data:
 * - 5 minute stale time (data doesn't change frequently)
 * - Retry failed requests 1 time
 * - Refetch on window focus (catch updates when user returns)
 */

import { useAuth } from '@clerk/nextjs';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Watches Clerk session identity (userId + orgId) and clears the
 * React Query cache whenever either changes.  This prevents stale
 * data from a previous user or org from persisting across sign-in /
 * sign-out / org-switch transitions.
 *
 * Important: do NOT treat Clerk's initial hydration (undefined → loaded)
 * as a session switch — that races public pages (e.g. /share/[token])
 * and wipes in-flight/completed fetches, leaving the UI stuck on loading.
 *
 * Public capability-URL queries (`public-share`) are not session-scoped
 * and must survive auth identity changes.
 */
function SessionCacheGuard() {
  const { userId, orgId, isLoaded } = useAuth();
  const queryClient = useQueryClient();
  const prevRef = useRef<{ userId: string | null; orgId: string | null } | null>(null);

  useEffect(() => {
    if (!isLoaded) return;

    const current = { userId: userId ?? null, orgId: orgId ?? null };

    // First loaded identity: baseline only — no cache wipe.
    if (prevRef.current === null) {
      prevRef.current = current;
      return;
    }

    const prev = prevRef.current;
    // Clerk often hydrates orgId a tick after isLoaded (null → org_xxx).
    // That is not an org switch — wiping here restarts every boot fetch.
    if (prev.userId === current.userId && prev.orgId == null && current.orgId != null) {
      prevRef.current = current;
      return;
    }
    if (prev.userId !== current.userId || prev.orgId !== current.orgId) {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'public-share',
      });
      prevRef.current = current;
    }
  }, [userId, orgId, isLoaded, queryClient]);

  return null;
}

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  // Create QueryClient inside component to avoid shared state between requests
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // B2B data doesn't change every second - 5 min stale time
            staleTime: 5 * 60 * 1000,
            // Cache for 30 minutes
            gcTime: 30 * 60 * 1000,
            // Retry failed requests once
            retry: 1,
            // Refetch when user returns to tab
            refetchOnWindowFocus: true,
            // Don't refetch on mount if data exists
            refetchOnMount: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <SessionCacheGuard />
      {children}
    </QueryClientProvider>
  );
}

